// LanguageSwitcher — guest app.
// Uses Chakra UI (the app's component library), not MUI.
// Mirrors the booking_engine LanguageSwitcher behaviour:
//   - hidden when only one language is available
//   - stores choice in localStorage via i18next detector
import React from 'react';
import {
  Box,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n';

interface LanguageSwitcherProps {
  /** Compact mode: show globe icon + code only. Default: false (shows native label). */
  compact?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ compact = false }) => {
  const { i18n, t } = useTranslation();

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const selectedColor = useColorModeValue('blue.700', 'blue.200');

  if (SUPPORTED_LANGUAGES.length <= 1) return null;

  const current = (i18n.language || 'en').split('-')[0];
  const currentLang =
    SUPPORTED_LANGUAGES.find((l) => l.code === current) ?? SUPPORTED_LANGUAGES[0];

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Box as={Globe} w={4} h={4} />}
          aria-label={t('language.switchTo')}
          fontWeight="medium"
          px={2}
        >
          {compact ? current.toUpperCase() : currentLang.nativeLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        w="160px"
        border="1px solid"
        borderColor={borderColor}
        shadow="md"
        _focus={{ outline: 'none' }}
      >
        <PopoverBody p={1}>
          <VStack spacing={0} align="stretch">
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isSelected = lang.code === current;
              return (
                <Box
                  key={lang.code}
                  as="button"
                  px={3}
                  py={2}
                  borderRadius="md"
                  textAlign="left"
                  bg={isSelected ? selectedBg : 'transparent'}
                  color={isSelected ? selectedColor : 'inherit'}
                  fontWeight={isSelected ? 600 : 400}
                  _hover={{ bg: isSelected ? selectedBg : hoverBg }}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  aria-pressed={isSelected}
                >
                  <Text fontSize="sm">{lang.nativeLabel}</Text>
                  {lang.nativeLabel !== lang.label && (
                    <Text fontSize="xs" opacity={0.6}>
                      {lang.label}
                    </Text>
                  )}
                </Box>
              );
            })}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default LanguageSwitcher;
